import { useState, useRef, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useScheduleTab } from '@/hooks/useScheduleTab';
import { useStageScheduleChecklist } from '@/hooks/useStageScheduleChecklist';
import { type ScheduleItem } from '@/lib/scheduleUtils';
import ScheduleItemModal from './schedule/ScheduleItemModal';
import ScheduleGanttEditModal from './schedule/ScheduleGanttEditModal';
import ScheduleGenerateModal from './schedule/ScheduleGenerateModal';
import ScheduleBaselineModal from './schedule/ScheduleBaselineModal';
import ScheduleLinkModal from './schedule/ScheduleLinkModal';
import ScheduleSettingsModal from './schedule/ScheduleSettingsModal';
import ScheduleAlertsPanel from './schedule/ScheduleAlertsPanel';
import ScheduleChangeLogPanel from './schedule/ScheduleChangeLogPanel';
import ScheduleListView from './schedule/ScheduleListView';
import ScheduleMobileListView from './schedule/ScheduleMobileListView';
import ScheduleSummaryCards from './schedule/ScheduleSummaryCards';
import ScheduleToolbar from './schedule/ScheduleToolbar';
import ScheduleClientView from './schedule/ScheduleClientView';
import ScheduleSubcontractorView from './schedule/ScheduleSubcontractorView';
import ScheduleGanttView from './schedule/ScheduleGanttView';
import BuildertrendImportModal from './schedule/BuildertrendImportModal';
import ScheduleHistoryDrawer from './schedule/ScheduleHistoryDrawer';
import ScheduleFilterDrawer from './schedule/ScheduleFilterDrawer';
import ScheduleExportPanel from './schedule/ScheduleExportPanel';
import ScheduleCompletionBlockModal from './schedule/ScheduleCompletionBlockModal';
import type { GanttRef } from './schedule/ScheduleGanttView';
import { isScheduleItemFinalComplete } from '@/lib/scheduleChecklistProgress';
import { copyGanttColorsFromMethuen } from '@/lib/copyGanttColors';
import { JPH_DEBUG } from '@/lib/jphDebug';

interface Props {
  projectId: string;
  canEdit?: boolean;
  viewerRole?: 'management' | 'subcontractor' | 'client';
  projectType?: string;
  projectName?: string;
  onSendInstruction?: (item: ScheduleItem) => void;
  /** Called after any schedule item is updated — triggers header reload */
  onScheduleUpdated?: () => void;
}

export default function ScheduleTab({ projectId, canEdit = true, viewerRole = 'management', projectType, projectName = 'Project', onSendInstruction, onScheduleUpdated }: Props) {
  const { user, profile } = useAuth();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const s = useScheduleTab(projectId, projectType, canEdit, viewerRole);
  const isMobile = useMediaQuery('(max-width: 768px)');
  const addTaskHandledRef = useRef(false);
  const isManualScheduleType = projectType === 'Renovation' || projectType === 'Other';

  const openAddFirstTask = useCallback(() => {
    s.openCreateItem();
  }, [s.openCreateItem]);

  useEffect(() => {
    if (addTaskHandledRef.current || s.loading) return;
    if (searchParams.get('addTask') !== '1' || !s.canEditReal || s.includedItems.length > 0) return;
    addTaskHandledRef.current = true;
    openAddFirstTask();
    const next = new URLSearchParams(searchParams);
    next.delete('addTask');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, s.loading, s.canEditReal, s.includedItems.length, openAddFirstTask]);

  const ganttViewHandledRef = useRef(false);
  useEffect(() => {
    if (ganttViewHandledRef.current || s.loading) return;
    if (searchParams.get('view') !== 'gantt') return;
    ganttViewHandledRef.current = true;
    s.setViewMode('gantt');
    const next = new URLSearchParams(searchParams);
    next.delete('view');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, s.loading, s.setViewMode]);

  // Defer heavy stage/checklist aggregation until a schedule mutation may need stage prompt.
  const [stageHookEnabled, setStageHookEnabled] = useState(false);
  const [showStagePrompt, setShowStagePrompt] = useState(false);
  const [pendingStageName, setPendingStageName] = useState<string | null>(null);
  const pendingStageCheckRef = useRef(false);

  const ensureStageHookEnabled = useCallback(() => {
    setStageHookEnabled(true);
  }, []);

  const {
    suggestedCurrentStage,
    projectStage,
    updateProjectStage,
    reload: reloadStages,
    loading: stageHookLoading,
  } = useStageScheduleChecklist(projectId, { enabled: stageHookEnabled });

  useEffect(() => {
    if (pendingStageCheckRef.current && stageHookEnabled && !stageHookLoading) {
      pendingStageCheckRef.current = false;
      if (projectStage && suggestedCurrentStage && suggestedCurrentStage !== projectStage) {
        setPendingStageName(suggestedCurrentStage);
        setShowStagePrompt(true);
      }
    }
  }, [stageHookEnabled, stageHookLoading, projectStage, suggestedCurrentStage]);

  const requestStagePromptCheck = useCallback(() => {
    pendingStageCheckRef.current = true;
    ensureStageHookEnabled();
  }, [ensureStageHookEnabled]);

  // Track if this is the first load so we can show skeleton vs keep data visible.
  // Initialize to true if items are already available from cache.
  const [firstLoadDone, setFirstLoadDone] = useState(s.items.length > 0);
  const prevLoadingRef = useRef(!s.items.length);

  useEffect(() => {
    if (prevLoadingRef.current && !s.itemsLoading) {
      setFirstLoadDone(true);
    }
    prevLoadingRef.current = s.itemsLoading;
  }, [s.itemsLoading]);

  useEffect(() => {
    if (s.items.length > 0) {
      setFirstLoadDone(true);
    }
  }, [s.items.length]);

  const [showImportModal, setShowImportModal] = useState(false);

  // Copy Gantt colors from 5 Methuen Road
  const [showCopyColorsConfirm, setShowCopyColorsConfirm] = useState(false);
  const [copyColorsLoading, setCopyColorsLoading] = useState(false);
  const [copyColorsResult, setCopyColorsResult] = useState<{ message: string; success: boolean } | null>(null);

  const ganttRef = useRef<GanttRef>(null);
  const ganttWorkspaceRef = useRef<HTMLDivElement>(null);
  const prevViewModeRef = useRef(s.viewMode);
  const ganttScrollDoneRef = useRef(false);

  // Confirmation modal state for "Mark All Pending Complete"
  const [showMarkAllConfirm, setShowMarkAllConfirm] = useState(false);

  // Collapsible schedule details — persisted to localStorage per project
  const [statusExpanded, setStatusExpandedState] = useState<boolean>(() => {
    try {
      const saved = localStorage.getItem(`jph_schedule_summary_details_visible_${projectId}`);
      return saved === 'true';
    } catch { return false; }
  });

  const setStatusExpanded = (v: boolean) => {
    setStatusExpandedState(v);
    try {
      localStorage.setItem(`jph_schedule_summary_details_visible_${projectId}`, String(v));
    } catch { /* ignore */ }
  };

  // ── Compute pending count using the SAME shared completion logic ──
  // An item is pending ONLY if isScheduleItemFinalComplete returns false.
  const pendingCount = s.items.filter((i) => {
    const cp = s.checklistProgressMap.get(i.id);
    const linked = cp ? (s.linkedChecklists?.get(i.id) ?? []) : [];
    const isComplete = isScheduleItemFinalComplete(
      { is_complete: i.is_complete, progress: i.progress, actual_end_date: i.actual_end_date },
      linked.map((l) => ({
        id: l.id,
        title: l.title,
        is_completed: l.is_completed,
        management_checked: l.management_checked,
        subcontractor_checked: l.subcontractor_checked,
        category: l.category,
        checklistTitle: l.checklistTitle,
        confidence: l.confidence,
        mappingType: l.mappingType,
        mappingId: l.mappingId,
        requires_management_check: l.requires_management_check,
        requires_subcontractor_check: l.requires_subcontractor_check,
      }))
    );
    return !isComplete;
  }).length;

  // ── Helper: unified completion check for banner (matches isScheduleItemFinalComplete) ──
  function isItemComplete(item: ScheduleItem): boolean {
    if (item.is_complete) return true;
    if (item.progress >= 100) return true;
    if (item.actual_end_date != null && item.actual_end_date !== '') return true;
    const cp = s.checklistProgressMap.get(item.id);
    const linked = cp ? (s.linkedChecklists?.get(item.id) ?? []) : [];
    if (linked.length > 0) {
      const allApproved = linked.every((l) => {
        const mgmtOk = l.requires_management_check !== false ? l.management_checked : true;
        const subOk = l.requires_subcontractor_check === true ? l.subcontractor_checked : true;
        return mgmtOk && subOk;
      });
      if (allApproved) return true;
    }
    return false;
  }

  // ── Progress explanation values ──
  const scheduleItemsForProgress = s.includedItems.length > 0 ? s.includedItems : s.items;
  const totalScheduleItems = scheduleItemsForProgress.length;
  const completedScheduleItems = totalScheduleItems > 0
    ? scheduleItemsForProgress.filter(isItemComplete).length
    : 0;
  const scheduleFinalProgress = totalScheduleItems > 0
    ? Math.round((completedScheduleItems / totalScheduleItems) * 100)
    : 0;
  const checklistFinalProgress = scheduleItemsForProgress.length > 0
    ? Math.round(
        scheduleItemsForProgress.reduce((sum, item) => {
          const cp = s.checklistProgressMap.get(item.id);
          if (!cp?.hasLinkedItems) return sum;
          return sum + (cp.progressPct / Math.max(scheduleItemsForProgress.filter((i) => s.checklistProgressMap.get(i.id)?.hasLinkedItems).length, 1));
        }, 0)
      )
    : 0;
  // Use the actual checklist progress from useScheduleChecklistProgress
  const actualChecklistFinal = (() => {
    let totalLinked = 0;
    let totalPct = 0;
    for (const item of scheduleItemsForProgress) {
      const cp = s.checklistProgressMap.get(item.id);
      if (cp?.hasLinkedItems) {
        totalLinked += 1;
        totalPct += cp.progressPct;
      }
    }
    return totalLinked > 0 ? Math.round(totalPct / totalLinked) : 0;
  })();
  const projectProgress = Math.round(scheduleFinalProgress * 0.5 + actualChecklistFinal * 0.5);

  // ── Status summary values for collapsible banner ──
  const allScheduleComplete = scheduleItemsForProgress.length > 0 && scheduleItemsForProgress.every((i) => isItemComplete(i));
  const noMappingItems = scheduleItemsForProgress.filter((i) => {
    const cp = s.checklistProgressMap.get(i.id);
    return !cp?.hasLinkedItems;
  });
  const noMappingCount = noMappingItems.length;
  const noMappingButComplete = noMappingItems.filter((i) => isItemComplete(i)).length;
  const noMappingAndIncomplete = noMappingItems.filter((i) => !isItemComplete(i)).length;
  const incompleteClItems = scheduleItemsForProgress.filter((i) => {
    const cp = s.checklistProgressMap.get(i.id);
    return cp?.hasLinkedItems && !cp?.allRequiredComplete && !isItemComplete(i);
  });
  const incompleteClCount = incompleteClItems.length;
  // NOTE: No auto-expand — details are user-controlled and persisted to localStorage

  const handleScrollToToday = () => {
    ganttRef.current?.scrollToToday();
  };

  useEffect(() => {
    if (s.viewMode !== 'gantt') {
      ganttScrollDoneRef.current = false;
      prevViewModeRef.current = s.viewMode;
      return;
    }
    if (s.isFullscreen) return;

    const switchedToGantt = prevViewModeRef.current !== 'gantt';
    prevViewModeRef.current = s.viewMode;
    if (!switchedToGantt && ganttScrollDoneRef.current) return;

    const delayMs = s.loading ? 450 : 120;
    const timer = window.setTimeout(() => {
      if (s.viewMode !== 'gantt' || s.isFullscreen) return;
      ganttWorkspaceRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      ganttScrollDoneRef.current = true;
    }, delayMs);

    return () => window.clearTimeout(timer);
  }, [s.viewMode, s.isFullscreen, s.loading]);

  const hasActiveFilters = s.hasActiveFilters;
  const filtersHideAllRows = s.filtersHideAllRows;

  // Schedule items load independently of settings/sidebar. Only block UI when items are still unknown.
  const isFirstLoad = s.itemsLoading && s.items.length === 0 && !firstLoadDone;
  const isBackgroundRefresh = s.itemsLoading && firstLoadDone && s.items.length > 0;

  if (isFirstLoad) {
    return (
      <div className="h-full flex flex-col items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <i className="ri-loader-4-line animate-spin text-[#E8621A] text-3xl"></i>
          <div className="space-y-2">
            <div className="h-3 bg-gray-200 rounded w-48 animate-pulse"></div>
            <div className="h-3 bg-gray-200 rounded w-32 animate-pulse"></div>
          </div>
        </div>
      </div>
    );
  }

  if (s.error && s.items.length === 0 && !s.itemsLoading) {
    return (
      <div className="text-center h-full flex flex-col items-center justify-center px-4">
        <p className="text-[#EF4444] text-sm">{s.error}</p>
        <button onClick={() => void s.reload()} className="mt-3 text-[#E8621A] text-sm font-medium cursor-pointer">{t('schedule.retry')}</button>
      </div>
    );
  }

  if (s.effectiveRole === 'client') {
    return (
      <div className="h-full overflow-y-auto overflow-x-hidden px-4 py-4">
        <ScheduleClientView
          items={s.items}
          settings={s.defaultSettings}
          holidays={s.allHolidays}
          healthSummary={s.healthSummary}
          criticalItemIds={s.criticalItemIds}
          projectEndDate={null}
          loading={false}
        />
      </div>
    );
  }

  if (s.effectiveRole === 'subcontractor') {
    return (
      <div className="h-full flex flex-col min-h-0 overflow-hidden">
        <ScheduleSubcontractorView
          items={s.items}
          links={s.links}
          settings={s.defaultSettings}
          holidays={s.allHolidays}
          profile={profile}
          loading={false}
        />
      </div>
    );
  }

  const hasBaseline = s.items.some((i) => i.baseline_start_date || i.baseline_end_date);

  function checkAndShowStagePrompt() {
    if (!stageHookEnabled) {
      requestStagePromptCheck();
      return;
    }
    if (!projectStage || !suggestedCurrentStage) return;
    if (suggestedCurrentStage !== projectStage) {
      setPendingStageName(suggestedCurrentStage);
      setShowStagePrompt(true);
    }
  }

  const wrappedHandleSaveItem = async (...args: Parameters<typeof s.handleSaveItem>) => {
    const success = await s.handleSaveItem(...args);
    if (success) {
      s.setShowItemModal(false);
      s.setEditItem(null);
      s.clearCreateDefaults();
    }
    if (stageHookEnabled) {
      await reloadStages();
    }
    checkAndShowStagePrompt();
    onScheduleUpdated?.();
  };

  const wrappedHandleToggleComplete = async (item: ScheduleItem, checked: boolean) => {
    await s.handleToggleComplete(item, checked);
    if (stageHookEnabled) {
      await reloadStages();
    }
    checkAndShowStagePrompt();
    onScheduleUpdated?.(); // Notify header to reload
  };

  const handleClearAllFilters = () => {
    s.clearAllFilters();
  };

  const renderFilterEmptyState = () => (
    <div className="w-full px-4 pt-6 pb-4 flex items-center justify-center">
      <div className="text-center max-w-md">
        <p className="text-[#374151] text-sm font-medium">
          {s.items.length} schedule {s.items.length === 1 ? 'item is' : 'items are'} hidden by the current filters.
        </p>
        <p className="text-[#9CA3AF] text-xs mt-1">
          {t('schedule.noItemsMatchCurrentFilter')}
        </p>
        <button
          type="button"
          onClick={handleClearAllFilters}
          className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#E8621A] text-white text-xs font-semibold cursor-pointer hover:bg-[#B84C10] transition-colors"
        >
          <i className="ri-close-circle-line"></i>
          {t('schedule.clearFiltersAndShowAll', { defaultValue: 'Clear filters and show all items' })}
        </button>
      </div>
    </div>
  );

  const handleCopyColors = async () => {
    setCopyColorsLoading(true);
    setCopyColorsResult(null);
    try {
      const result = await copyGanttColorsFromMethuen(projectId);
      setCopyColorsResult({ message: result.message, success: result.success });
      if (result.success) {
        s.reload();
      }
    } catch (err: unknown) {
      setCopyColorsResult({ message: err instanceof Error ? err.message : 'Copy failed', success: false });
    } finally {
      setCopyColorsLoading(false);
      setShowCopyColorsConfirm(false);
    }
  };

  const handleApplyFilter = () => {
    // Filters are already applied reactively; close panel
  };

  const dateRangeStart = s.filteredItems.length > 0
    ? s.filteredItems.filter((i) => i.start_date).sort((a, b) => (a.start_date || '').localeCompare(b.start_date || ''))[0]?.start_date || null
    : null;
  const dateRangeEnd = s.filteredItems.length > 0
    ? s.filteredItems.filter((i) => i.end_date).sort((a, b) => (b.end_date || '').localeCompare(a.end_date || ''))[0]?.end_date || null
    : null;

  return (
    <div className={`flex flex-col w-full max-w-full ${s.isFullscreen ? 'fixed inset-0 z-[200] bg-[#F5F5F3] flex flex-col overflow-hidden h-[100dvh]' : 'overflow-x-hidden'}`}>
      {/* Subtle background refresh indicator */}
      {isBackgroundRefresh && (
        <div className="flex-shrink-0 bg-[#FEF0E7]/60 border-b border-[#FDBA74]/30 px-4 py-1 flex items-center justify-center gap-2">
          <i className="ri-loader-4-line animate-spin text-[#E8621A] text-xs"></i>
          <span className="text-[#92400E] text-[11px] font-medium">{t('schedule.refreshingScheduleData')}</span>
        </div>
      )}

      {/* Toolbar */}
      <div className="flex-shrink-0 bg-white border-b border-gray-200 px-4 py-2">
        {s.scheduleUndoSnapshot && s.scheduleUndoSnapshot.size > 0 && (
          <div className="mb-2 flex items-center justify-between gap-2 rounded-lg border border-[#BAE6FD] bg-[#F0F9FF] px-3 py-2">
            <span className="text-[11px] text-[#0369A1] font-medium">Linked schedule dates were updated.</span>
            <button
              type="button"
              onClick={() => void s.handleScheduleUndo()}
              disabled={s.saving}
              className="px-2.5 py-1 rounded-lg bg-white border border-[#7DD3FC] text-[#0369A1] text-[11px] font-bold cursor-pointer hover:bg-[#E0F2FE] disabled:opacity-50"
            >
              Undo last change
            </button>
          </div>
        )}
        <ScheduleToolbar
          itemCount={s.items.length}
          canEdit={s.canEditReal}
          viewMode={s.viewMode}
          onToggleView={() => s.setViewMode(s.viewMode === 'list' ? 'gantt' : 'list')}
          onRefresh={s.reload}
          onGenerateTimeline={() => s.setShowGenerateModal(true)}
          onImportBuildertrend={() => setShowImportModal(true)}
          completionFilter={s.completionFilter}
          onSetCompletionFilter={s.setCompletionFilter}
          mainStageFilter={s.mainStageFilter}
          onSetMainStageFilter={s.setMainStageFilter}
          constructionSubStageFilter={s.constructionSubStageFilter}
          onSetConstructionSubStageFilter={s.setConstructionSubStageFilter}
          onToggleFullscreen={s.toggleFullscreen}
          isFullscreen={s.isFullscreen}
          onIncludeAll={s.handleIncludeAll}
          onExcludeAll={s.handleExcludeAll}
          showExcluded={s.showExcluded}
          onToggleShowExcluded={() => s.setShowExcluded(!s.showExcluded)}
          onRecalcCriticalPath={s.handleRecalcCriticalPath}
          onRecalcLinks={s.handleRecalc}
          onRecalculateProgress={s.handleRecalculateProgress}
          onSetBaseline={() => s.setShowBaselineModal(true)}
          onToggleDebug={JPH_DEBUG ? () => s.setShowDebug(!s.showDebug) : undefined}
          onAddItem={() => { s.openCreateItem(); }}
          saving={s.saving}
          showAllLinks={s.showAllLinks}
          onToggleShowAllLinks={() => s.setShowAllLinks(!s.showAllLinks)}
          isMobile={isMobile}
          showSummary={statusExpanded}
          onToggleSummary={() => setStatusExpanded(!statusExpanded)}
          // New utility features
          ganttScale={s.ganttScale}
          onSetGanttScale={s.setGanttScale}
          showPhases={s.showPhases}
          onToggleShowPhases={() => s.setShowPhases(!s.showPhases)}
          criticalPathEnabled={s.criticalPathEnabled}
          showCriticalPathOnly={s.showCriticalPathOnly}
          onToggleCriticalPathOnly={() => s.setShowCriticalPathOnly(!s.showCriticalPathOnly)}
          onResetCriticalPathFlags={s.handleResetCriticalPathFlags}
          onOpenFilterPanel={() => s.setFilterPanelOpen(true)}
          onOpenHistoryPanel={() => s.setHistoryPanelOpen(true)}
          onOpenExportPanel={() => s.setExportPanelOpen(true)}
          hasActiveFilters={hasActiveFilters}
          onScrollToToday={handleScrollToToday}
          criticalItemCount={s.criticalItemIds.size}
          totalItemCount={s.items.length}
          // NEW pending filter
          pendingFilter={s.pendingFilter}
          onSetPendingFilter={s.setPendingFilter}
          // NEW bulk selection
          selectedItemIds={s.selectedItemIds}
          onToggleSelectItem={s.toggleSelectItem}
          onSelectAllVisible={s.selectAllVisible}
          onClearSelection={s.clearSelection}
          onBulkMarkComplete={() => { s.handleBulkMarkComplete(Array.from(s.selectedItemIds)); onScheduleUpdated?.(); }}
          onMarkAllPendingComplete={() => setShowMarkAllConfirm(true)}
          pendingCount={pendingCount}
          bulkMarking={s.bulkMarking}
          canBulkSelect={s.isManagement}
          onCopyGanttColorsFromMethuen={s.isManagement ? () => setShowCopyColorsConfirm(true) : undefined}
        />
      </div>

      {/* Stage completion prompt */}
      {showStagePrompt && pendingStageName && (
        <div className="flex-shrink-0 px-4 pt-3">
          <div className="bg-[#FEF0E7] rounded-2xl border border-[#FDBA74] p-4">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-xl bg-[#FEF0E7] border border-[#FDBA74] flex items-center justify-center flex-shrink-0">
                <i className="ri-lightbulb-line text-[#E8621A] text-lg"></i>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[#1A1A1A] text-sm font-semibold">
                  {t('schedule.scheduleProgressSuggestsMoving', { stage: pendingStageName })}
                </p>
                <div className="flex items-center gap-2 mt-2">
                  <button
                    onClick={() => setShowStagePrompt(false)}
                    className="px-3 py-1.5 rounded-lg bg-white border border-gray-200 text-[#374151] text-xs font-bold cursor-pointer whitespace-nowrap"
                  >
                    {t('schedule.dismiss')}
                  </button>
                  <button
                    onClick={async () => {
                      const ok = await updateProjectStage(pendingStageName);
                      if (ok) {
                        setShowStagePrompt(false);
                        setPendingStageName(null);
                      }
                    }}
                    className="px-3 py-1.5 rounded-lg bg-[#E8621A] text-white text-xs font-bold cursor-pointer whitespace-nowrap"
                  >
                    {t('schedule.updateProjectStage')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Compact Schedule Summary — hidden in Gantt view to maximize chart space ── */}
      {s.isManagement && s.items.length > 0 && s.viewMode !== 'gantt' && (
        <div className="flex-shrink-0 px-4 pt-3">
          {/* Compact top row */}
          <div className="bg-white rounded-2xl border border-gray-200 px-4 py-2.5">
            <div className="flex items-center gap-2 flex-wrap">
              {/* Status icon */}
              {allScheduleComplete && pendingCount === 0 ? (
                <div className="w-6 h-6 rounded-lg bg-[#F0FDF4] border border-[#059669]/20 flex items-center justify-center flex-shrink-0">
                  <i className="ri-checkbox-circle-line text-[#059669] text-sm"></i>
                </div>
              ) : (
                <div className="w-6 h-6 rounded-lg bg-[#FFFBEB] border border-[#F59E0B]/20 flex items-center justify-center flex-shrink-0">
                  <i className="ri-error-warning-line text-[#F59E0B] text-sm"></i>
                </div>
              )}

              {/* Key metrics — inline pills */}
              <div className="flex items-center gap-3 flex-wrap flex-1 min-w-0">
                <span className="text-xs font-bold text-[#1A1A1A]">
                  {t('schedule.projectProgressColon')} <span className={projectProgress === 100 ? 'text-[#059669]' : 'text-[#E8621A]'}>{projectProgress}%</span>
                </span>
                <span className="text-[#E5E7EB] hidden sm:inline">|</span>
                <span className="text-xs font-semibold text-[#6B7280] hidden sm:inline">
                  {t('schedule.scheduleFinalColon')} <span className={scheduleFinalProgress === 100 ? 'text-[#059669]' : 'text-[#374151]'}>{scheduleFinalProgress}%</span>
                </span>
                <span className="text-[#E5E7EB] hidden sm:inline">|</span>
                <span className="text-xs font-semibold text-[#6B7280] hidden sm:inline">
                  {t('schedule.checklistFinalColon')} <span className={actualChecklistFinal === 100 ? 'text-[#059669]' : 'text-[#374151]'}>{actualChecklistFinal}%</span>
                </span>
                {pendingCount > 0 && (
                  <>
                    <span className="text-[#E5E7EB] hidden sm:inline">|</span>
                    <span className="text-xs font-semibold text-[#F59E0B]">
                      {t('schedule.pendingCountLabel', { count: pendingCount })}
                    </span>
                  </>
                )}
              </div>

              {/* Show/Hide details button */}
              <button
                onClick={() => setStatusExpanded(!statusExpanded)}
                className="flex-shrink-0 flex items-center gap-1 px-2.5 py-1 rounded-lg bg-gray-50 border border-gray-200 text-[#374151] text-xs font-medium cursor-pointer whitespace-nowrap hover:bg-gray-100 transition-colors"
              >
                {statusExpanded ? (
                  <><i className="ri-arrow-up-s-line"></i><span className="hidden sm:inline">{t('schedule.statusHideDetails')}</span><span className="sm:hidden">{t('schedule.hideDetailsBtn2')}</span></>
                ) : (
                  <><i className="ri-arrow-down-s-line"></i><span className="hidden sm:inline">{t('schedule.statusShowDetails')}</span><span className="sm:hidden">{t('schedule.showDetailsBtn2')}</span></>
                )}
              </button>
            </div>
          </div>

          {/* Collapsible detailed section */}
          {statusExpanded && (
            <div className="mt-2 space-y-2">
              {/* Progress formula explanation */}
              <div className="bg-[#F9FAFB] rounded-xl border border-gray-200 px-4 py-3">
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="w-8 h-8 rounded-lg bg-white border border-gray-200 flex items-center justify-center flex-shrink-0">
                    <i className="ri-bar-chart-grouped-line text-[#6B7280] text-base"></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="text-xs font-bold text-[#1A1A1A]">
                        {t('schedule.checklistFinalColon')} <span className={actualChecklistFinal === 100 ? 'text-[#059669]' : 'text-[#E8621A]'}>{actualChecklistFinal}%</span>
                      </span>
                      <span className="text-[#D1D5DB]">|</span>
                      <span className="text-xs font-bold text-[#1A1A1A]">
                        {t('schedule.scheduleFinalColon')} <span className={scheduleFinalProgress === 100 ? 'text-[#059669]' : 'text-[#E8621A]'}>{scheduleFinalProgress}%</span>
                      </span>
                      <span className="text-[#D1D5DB]">|</span>
                      <span className="text-xs font-bold text-[#1A1A1A]">
                        {t('schedule.projectProgressColon')} <span className={projectProgress === 100 ? 'text-[#059669]' : 'text-[#E8621A]'}>{projectProgress}%</span>
                      </span>
                    </div>
                    <p className="text-[11px] text-[#9CA3AF] mt-0.5">{t('schedule.calculatedFrom50Pct')}</p>
                  </div>
                </div>
              </div>

              {/* Status detail block */}
              {allScheduleComplete && pendingCount === 0 ? (
                <div className="bg-[#F0FDF4] rounded-xl border border-[#BBF7D0] p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-[#F0FDF4] border border-[#059669]/30 flex items-center justify-center flex-shrink-0">
                      <i className="ri-checkbox-circle-line text-[#059669] text-base"></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[#1A1A1A] text-sm font-semibold">
                        {t('schedule.scheduleCompleteAll', { pct: scheduleFinalProgress, total: totalScheduleItems })}
                      </p>
                      {noMappingCount > 0 && (
                        <p className="text-[#059669] text-xs mt-1 leading-relaxed">
                          <i className="ri-information-line mr-1"></i>
                          {noMappingCount} item{noMappingCount > 1 ? 's are' : ' is'} not linked to checklist items. They still count as complete.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-[#FFFBEB] rounded-xl border border-[#FDE68A] p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-[#FEF0E7] border border-[#FDBA74] flex items-center justify-center flex-shrink-0">
                      <i className="ri-error-warning-line text-[#F59E0B] text-base"></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[#1A1A1A] text-sm font-semibold">
                        {t('schedule.pendingItemsCount', { count: pendingCount, plural: pendingCount > 1 ? 's' : '' })}
                        {scheduleFinalProgress > 0 && (
                          <span className="text-[#9CA3AF] font-normal ml-1">({completedScheduleItems}/{totalScheduleItems} complete)</span>
                        )}
                      </p>
                      <p className="text-[#92400E] text-xs mt-0.5 leading-relaxed">
                        {noMappingAndIncomplete > 0 && <span>{noMappingAndIncomplete} item{noMappingAndIncomplete > 1 ? 's have' : ' has'} no checklist mapping and {noMappingAndIncomplete > 1 ? 'are' : 'is'} still incomplete. {' '}</span>}
                        {incompleteClCount > 0 && <span>{incompleteClCount} item{incompleteClCount > 1 ? 's have' : ' has'} incomplete linked checklist. {' '}</span>}
                        {noMappingButComplete > 0 && <span className="text-[#059669]">{noMappingButComplete} completed item{noMappingButComplete > 1 ? 's are' : ' is'} not linked to checklist items. They still count as complete.</span>}
                      </p>
                      <div className="mobile-btn-group mt-3">
                        {pendingCount > 0 ? (
                          <button
                            onClick={() => setShowMarkAllConfirm(true)}
                            className="mobile-cta-btn rounded-xl bg-[#059669] text-white text-xs font-bold cursor-pointer hover:bg-[#047857] transition-colors flex items-center justify-center gap-1.5"
                          >
                            <i className="ri-checkbox-circle-line flex-shrink-0"></i>
                            <span className="hidden sm:inline">{t('schedule.markAllPendingComplete')}</span>
                            <span className="sm:hidden">{t('mobile.markAllPendingComplete')}</span>
                          </button>
                        ) : (
                          <button disabled className="mobile-cta-btn rounded-xl bg-gray-100 text-[#9CA3AF] text-xs font-bold cursor-not-allowed flex items-center justify-center gap-1.5">
                            <i className="ri-checkbox-circle-line flex-shrink-0"></i>
                            {t('schedule.noPendingItems')}
                          </button>
                        )}
                        <button
                          onClick={() => { s.setPendingFilter('pending_final'); s.setViewMode('list'); }}
                          className="mobile-cta-btn rounded-xl bg-white border border-gray-200 text-[#374151] text-xs font-bold cursor-pointer hover:bg-gray-50 transition-colors flex items-center justify-center gap-1.5"
                        >
                          <i className="ri-search-line flex-shrink-0"></i>
                          <span className="hidden sm:inline">{t('schedule.reviewPendingItemsBtn')}</span>
                          <span className="sm:hidden">{t('mobile.reviewPendingItems')}</span>
                        </button>
                        {noMappingAndIncomplete > 0 && (
                          <button
                            onClick={() => { s.setPendingFilter('no_mapping'); s.setViewMode('list'); }}
                            className="mobile-cta-btn rounded-xl bg-white border border-gray-200 text-[#374151] text-xs font-bold cursor-pointer hover:bg-gray-50 transition-colors"
                          >
                            {t('schedule.noChecklistMappingBtn')}
                          </button>
                        )}
                        {incompleteClCount > 0 && (
                          <button
                            onClick={() => { s.setPendingFilter('incomplete_checklist'); s.setViewMode('list'); }}
                            className="mobile-cta-btn rounded-xl bg-white border border-gray-200 text-[#374151] text-xs font-bold cursor-pointer hover:bg-gray-50 transition-colors"
                          >
                            {t('schedule.incompleteChecklistBtn')}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Health/detailed metric cards — critical/delayed/at-risk cards */}
              <ScheduleSummaryCards
                includedItems={s.includedItems}
                criticalItemIds={s.criticalItemIds}
                healthSummary={s.healthSummary}
                viewerRole="management"
                settings={s.settings.id ? s.settings : undefined}
                holidays={s.allHolidays}
              />

              {/* Critical path alerts — only when enabled */}
              {!s.isSetupMode && s.criticalPathEnabled && (
                <ScheduleAlertsPanel
                  items={s.items}
                  computedRisk={s.computedRisk}
                  criticalItemIds={s.criticalItemIds}
                  healthSummary={s.healthSummary}
                  warning={null}
                  viewerRole="management"
                />
              )}
            </div>
          )}
        </div>
      )}

      {/* Workspace — no fixed height or overflow-hidden here; let the page scroll naturally on mobile */}
      <div className="flex flex-col w-full">
        {s.error && s.items.length > 0 && (
          <div className={`flex-shrink-0 px-4 pt-3 ${s.isFullscreen ? 'bg-[#F5F5F3]' : ''}`}>
            <div className="bg-[#FEF2F2] rounded-xl p-3 border border-[#FECACA] flex items-start gap-2">
              <i className="ri-error-warning-line text-[#EF4444] text-sm flex-shrink-0 mt-0.5"></i>
              <div className="flex-1 min-w-0">
                <p className="text-[#991B1B] text-xs font-medium">{s.error}</p>
                <p className="text-[#B91C1C]/80 text-[11px] mt-0.5">Showing the last loaded schedule. Refresh to try again.</p>
              </div>
              <button
                type="button"
                onClick={() => void s.reload()}
                className="flex-shrink-0 px-2.5 py-1 rounded-lg bg-white border border-[#FECACA] text-[#991B1B] text-[11px] font-bold cursor-pointer hover:bg-[#FEF2F2]"
              >
                {t('schedule.retry')}
              </button>
            </div>
          </div>
        )}

        {s.trulyAllExcluded && (
          <div className={`flex-shrink-0 px-4 pt-3 ${s.isFullscreen ? 'bg-[#F5F5F3]' : ''}`}>
            <div className="bg-[#FFFBEB] rounded-xl p-3 border border-[#FDE68A] flex items-start gap-2">
              <i className="ri-eye-off-line text-[#D97706] text-sm flex-shrink-0 mt-0.5"></i>
              <div className="flex-1 min-w-0">
                <p className="text-[#92400E] text-xs font-medium">
                  {s.items.length} schedule {s.items.length === 1 ? 'item is' : 'items are'} excluded from the Gantt view.
                </p>
                <p className="text-[#92400E]/80 text-[11px] mt-0.5">
                  These tasks still exist. Include them to show bars in the Gantt chart.
                </p>
                {s.saveError && <p className="text-[#EF4444] text-[11px] mt-1">{s.saveError}</p>}
                {s.saveSuccess && <p className="text-[#059669] text-[11px] mt-1">{s.saveSuccess}</p>}
              </div>
              {s.canEditReal && (
                <button
                  type="button"
                  onClick={() => void s.handleIncludeAll()}
                  disabled={s.saving}
                  className="flex-shrink-0 px-2.5 py-1 rounded-lg bg-[#E8621A] text-white text-[11px] font-bold cursor-pointer hover:bg-[#B84C10] disabled:opacity-60"
                >
                  {s.saving ? t('schedule.settingUpSchedule') : t('ganttView.includeAll', { defaultValue: 'Include all items' })}
                </button>
              )}
            </div>
          </div>
        )}

        {filtersHideAllRows && hasActiveFilters && (
          <div className={`flex-shrink-0 px-4 pt-3 ${s.isFullscreen ? 'bg-[#F5F5F3]' : ''}`}>
            <div className="bg-[#FFFBEB] rounded-xl p-3 border border-[#FDE68A] flex items-start gap-2">
              <i className="ri-filter-off-line text-[#D97706] text-sm flex-shrink-0 mt-0.5"></i>
              <div className="flex-1 min-w-0">
                <p className="text-[#92400E] text-xs font-medium">
                  {s.items.length} schedule {s.items.length === 1 ? 'item is' : 'items are'} hidden by the current filters.
                </p>
                <p className="text-[#92400E]/80 text-[11px] mt-0.5">{t('schedule.noItemsMatchCurrentFilter')}</p>
              </div>
              <button
                type="button"
                onClick={handleClearAllFilters}
                className="flex-shrink-0 px-2.5 py-1 rounded-lg bg-white border border-[#FDE68A] text-[#92400E] text-[11px] font-bold cursor-pointer hover:bg-[#FFFBEB]"
              >
                {t('schedule.clearFiltersAndShowAll', { defaultValue: 'Clear filters and show all items' })}
              </button>
            </div>
          </div>
        )}

        {s.notificationWarning && (
          <div className={`flex-shrink-0 px-4 pt-3 ${s.isFullscreen ? 'bg-[#F5F5F3]' : ''}`}>
            <div className="bg-[#FEF0E7] rounded-xl p-3 border border-[#FDE68A] flex items-start gap-2">
              <i className="ri-error-warning-line text-[#F59E0B] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-[#92400E] text-xs flex-1">{s.notificationWarning}</p>
              {s.scheduleUndoSnapshot && s.scheduleUndoSnapshot.size > 0 && (
                <button
                  type="button"
                  onClick={() => void s.handleScheduleUndo()}
                  disabled={s.saving}
                  className="flex-shrink-0 px-2.5 py-1 rounded-lg bg-white border border-[#FDBA74] text-[#92400E] text-[11px] font-bold cursor-pointer hover:bg-[#FFFBEB] disabled:opacity-50"
                >
                  Undo
                </button>
              )}
            </div>
          </div>
        )}

        {!s.isFullscreen && (
          <>
            {/* Setup mode notice — replaces noisy alerts for newly imported schedules */}
            {s.isSetupMode && (
              <div className="flex-shrink-0 px-4 pt-3">
                <div className="bg-[#F0F9FF] rounded-xl p-3 border border-[#BAE6FD] flex items-start gap-2">
                  <i className="ri-information-line text-[#0EA5E9] text-sm flex-shrink-0 mt-0.5"></i>
                  <div>
                    <p className="text-[#0369A1] text-xs font-semibold">{t('schedule.setupInProgressTitle')}</p>
                    <p className="text-[#0369A1]/80 text-xs mt-0.5">
                      {t('schedule.setupInProgressDesc')}
                    </p>
                  </div>
                </div>
              </div>
            )}


          </>
        )}

        {/* Main content */}
        {s.isManagement && s.items.length === 0 && !s.itemsLoading ? (
          <div className="w-full overflow-x-hidden px-4 pt-3">
            <div className="bg-white rounded-2xl border border-gray-100 p-6 text-center">
              <div className="w-14 h-14 flex items-center justify-center mx-auto mb-4 rounded-2xl bg-[#F9FAFB]">
                <i className="ri-calendar-todo-line text-[#D1D5DB] text-3xl"></i>
              </div>
              <h3 className="text-[#1A1A1A] font-bold text-base mb-2">{t('schedule.noScheduleYet')}</h3>
              <p className="text-[#9CA3AF] text-sm max-w-sm mx-auto mb-4">
                {isManualScheduleType
                  ? t('schedule.renovationEmptyState')
                  : projectType === 'Development'
                  ? t('schedule.createFromDevTemplate')
                  : t('schedule.noScheduleCreatedDesc')}
              </p>
              {!isManualScheduleType && (
                <p className="text-[#9CA3AF] text-xs max-w-sm mx-auto mb-4">
                  {t('schedule.applyPresetWarning')}
                </p>
              )}
              {s.scheduleSetupState === 'success' && (
                <div className="bg-[#F0FDF4] rounded-xl p-3 mb-4 text-left max-w-sm mx-auto">
                  <p className="text-[#059669] text-xs font-semibold">{s.scheduleSetupMessage}</p>
                </div>
              )}
              {s.scheduleSetupState === 'exists' && (
                <div className="bg-[#FEF0E7] rounded-xl p-3 mb-4 text-left max-w-sm mx-auto">
                  <p className="text-[#92400E] text-xs font-semibold">{s.scheduleSetupMessage}</p>
                </div>
              )}
              {s.scheduleSetupState === 'error' && (
                <div className="bg-[#FEF2F2] rounded-xl p-3 mb-4 text-left max-w-sm mx-auto">
                  <p className="text-[#EF4444] text-xs font-semibold">{s.scheduleSetupMessage || s.saveError}</p>
                </div>
              )}
              {isManualScheduleType ? (
                s.canEditReal && (
                  <button
                    onClick={openAddFirstTask}
                    disabled={s.saving}
                    className="mobile-cta-btn inline-flex items-center justify-center gap-2 rounded-xl bg-[#E8621A] text-white text-sm font-semibold cursor-pointer disabled:opacity-60 hover:bg-[#B84C10] transition-colors px-6 py-3"
                  >
                    <i className="ri-add-line flex-shrink-0 text-lg"></i>
                    {t('schedule.addFirstTaskBtn')}
                  </button>
                )
              ) : (
                <>
                  <button onClick={s.handleSetupSchedule} disabled={s.saving || s.scheduleSetupState === 'loading'}
                    className="mobile-cta-btn inline-flex items-center justify-center gap-2 rounded-xl bg-[#E8621A] text-white text-sm font-semibold cursor-pointer disabled:opacity-60 hover:bg-[#B84C10] transition-colors">
                    {s.scheduleSetupState === 'loading' ? (
                      <><i className="ri-loader-4-line animate-spin flex-shrink-0"></i>{t('schedule.settingUpSchedule')}</>
                    ) : (
                      <>
                        <i className="ri-calendar-todo-line flex-shrink-0"></i>
                        <span className="hidden sm:inline">{t('schedule.createFromDevTemplateBtn')}</span>
                        <span className="sm:hidden">{t('mobile.createScheduleFromTemplate')}</span>
                      </>
                    )}
                  </button>
                  {s.canEditReal && (
                    <button
                      onClick={openAddFirstTask}
                      disabled={s.saving}
                      className="mt-3 block mx-auto text-xs font-semibold text-[#E8621A] hover:underline disabled:opacity-50"
                    >
                      {t('schedule.addTaskManuallyInstead')}
                    </button>
                  )}
                  {s.scheduleSetupState === 'error' && (
                    <button
                      onClick={s.handleSetupSchedule}
                      disabled={s.saving}
                      className="mt-3 text-xs font-semibold text-[#E8621A] hover:underline disabled:opacity-50"
                    >
                      {t('checklist.retry')}
                    </button>
                  )}
                </>
              )}
              {s.saveError && s.scheduleSetupState !== 'error' && <p className="text-[#EF4444] text-xs mt-3">{s.saveError}</p>}
              {s.saveSuccess && s.scheduleSetupState !== 'success' && <p className="text-[#059669] text-xs mt-3">{s.saveSuccess}</p>}
            </div>
          </div>
        ) : filtersHideAllRows ? (
          renderFilterEmptyState()
        ) : s.isManagement && s.includedItems.length > 0 && !s.includedItems.some((i) => i.start_date) ? (
          <div className="w-full overflow-x-hidden px-4 pt-3">
            <div className="bg-[#FEF0E7] rounded-xl p-4 border border-[#FDE68A] flex items-start gap-3">
              <i className="ri-information-line text-[#92400E] text-lg flex-shrink-0"></i>
              <div className="flex-1">
                <p className="text-[#92400E] text-sm font-semibold">{t('schedule.scheduleItemsNeedDates')}</p>
                <p className="text-[#92400E]/80 text-xs mt-0.5">{t('schedule.scheduleItemsNeedDatesDesc', { count: s.includedItems.length })}</p>
                <button onClick={() => s.setShowGenerateModal(true)}
                  className="mobile-cta-btn mt-2 inline-flex items-center justify-center gap-1.5 rounded-lg bg-[#E8621A] text-white text-xs font-semibold cursor-pointer hover:bg-[#B84C10] transition-colors">
                  <i className="ri-calendar-event-line flex-shrink-0"></i>
                  <span className="hidden sm:inline">{t('schedule.generateInitialTimelineBtn')}</span>
                  <span className="sm:hidden">{t('mobile.generateInitialTimeline')}</span>
                </button>
              </div>
            </div>
          </div>
        ) : s.viewMode === 'gantt' ? (
          (
          <div
            ref={ganttWorkspaceRef}
            className={`w-full flex flex-col flex-1 min-h-0 ${s.isFullscreen ? 'overflow-hidden' : ''}`}
            style={{ scrollMarginTop: 88 }}
          >
            {/* Mobile swipe helper */}
            {isMobile && (
              <div className="flex-shrink-0 px-4 py-2 bg-[#F0F9FF] border-b border-[#BAE6FD]">
                <p className="text-[10px] text-[#0369A1] flex items-center gap-1.5">
                  <i className="ri-arrow-left-right-line"></i>
                  {t('schedule.ganttSwipeHint')}
                </p>
              </div>
            )}
            {/* ScheduleGanttView manages its own scroll — no outer overflow wrapper needed */}
            <ScheduleGanttView
              ref={ganttRef}
              items={s.filteredItems}
              allItems={s.items}
              links={s.links}
              showAllLinks={s.showAllLinks}
              settings={s.defaultSettings}
              holidays={s.allHolidays}
              criticalItemIds={s.criticalPathEnabled ? s.criticalItemIds : new Set()}
              computedRisk={s.criticalPathEnabled ? s.computedRisk : new Map()}
              canEdit={s.canEditReal}
              onEdit={(item) => { s.setEditItem(item); s.setShowItemModal(true); }}
              onToggleComplete={wrappedHandleToggleComplete}
              onToggleIncluded={s.handleToggleIncluded}
              onIncludeAll={s.handleIncludeAll}
              onExcludeAll={s.handleExcludeAll}
              showExcluded={s.showExcluded}
              isFullscreen={s.isFullscreen}
              scale={s.ganttScale}
              showPhases={s.showPhases}
              showCriticalPathOnly={s.showCriticalPathOnly}
              onRecalcCriticalPath={s.handleRecalcCriticalPath}
              checklistProgressMap={s.checklistProgressMap}
              onOpenCompletionBlock={s.onOpenCompletionBlock}
              onBarChange={s.handleGanttBarChange}
              onCreateAtDate={s.canEditReal ? s.openCreateItemAtDate : undefined}
              scheduleUpdating={s.saving}
            />
          </div>
          )
        ) : s.filteredItems.length > 0 ? (
          <div className="w-full overflow-x-hidden px-4 pt-3 pb-4">
            {isMobile ? (
              <ScheduleMobileListView
                items={s.filteredItems}
                links={s.links}
                canEdit={s.canEditReal}
                criticalItemIds={s.criticalPathEnabled ? s.criticalItemIds : new Set()}
                checklistProgressMap={s.checklistProgressMap}
                linkedChecklists={s.linkedChecklists}
                onEdit={(item) => { s.setEditItem(item); s.setShowItemModal(true); }}
                onToggleComplete={wrappedHandleToggleComplete}
                onOpenCompletionBlock={s.onOpenCompletionBlock}
                onSendInstruction={onSendInstruction}
                viewerRole={s.effectiveRole}
              />
            ) : (
              <ScheduleListView
                items={s.filteredItems}
                links={s.links}
                canEdit={s.canEditReal}
                criticalItemIds={s.criticalPathEnabled ? s.criticalItemIds : new Set()}
                computedRisk={s.criticalPathEnabled ? s.computedRisk : new Map()}
                checklistProgressMap={s.checklistProgressMap}
                linkedChecklists={s.linkedChecklists}
                onEdit={(item) => { s.setEditItem(item); s.setShowItemModal(true); }}
                onDelete={s.handleDelete}
                onAddLink={(item) => { s.setEditItem(item); s.setShowLinkModal(true); }}
                onRemoveLink={s.handleRemoveLink}
                onToggleComplete={wrappedHandleToggleComplete}
                onProgressChange={s.handleProgressChange}
                onOpenCompletionBlock={s.onOpenCompletionBlock}
                onReload={s.reload}
                onSendInstruction={onSendInstruction}
                viewerRole={s.effectiveRole}
                selectedItemIds={s.selectedItemIds}
                onToggleSelectItem={s.toggleSelectItem}
                canBulkSelect={s.isManagement}
              />
            )}
            {/* Lazy change log */}
            {!s.isFullscreen && s.isManagement && s.items.length > 0 && (
              <div className="pt-3">
                <button onClick={() => s.setShowChangeLog(!s.showChangeLog)}
                  className="flex items-center gap-1.5 text-[#9CA3AF] text-[10px] font-bold uppercase tracking-wider cursor-pointer hover:text-[#6B7280] transition-colors mb-2">
                  <i className={`ri-${s.showChangeLog ? 'arrow-down-s-line' : 'arrow-right-s-line'}`}></i>
                  {s.showChangeLog ? t('schedule.hideChangeHistory') : t('schedule.showChangeHistory')}
                </button>
                {s.showChangeLog && (
                  <div className="bg-white rounded-2xl border border-gray-100 p-4">
                    <ScheduleChangeLogPanel projectId={projectId} />
                  </div>
                )}
              </div>
            )}
          </div>
        ) : filtersHideAllRows ? (
          renderFilterEmptyState()
        ) : null}
      </div>

      {/* Side drawer: History */}
      <ScheduleHistoryDrawer
        projectId={projectId}
        isOpen={s.historyPanelOpen}
        onClose={() => s.setHistoryPanelOpen(false)}
      />

      {/* Side drawer: Filter */}
      <ScheduleFilterDrawer
        isOpen={s.filterPanelOpen}
        onClose={() => s.setFilterPanelOpen(false)}
        items={s.items}
        filterTitleSearch={s.filterTitleSearch}
        onSetFilterTitleSearch={s.setFilterTitleSearch}
        filterAssignedTo={s.filterAssignedTo}
        onSetFilterAssignedTo={s.setFilterAssignedTo}
        filterStatus={s.filterStatus}
        onSetFilterStatus={s.setFilterStatus}
        filterPhase={s.filterPhase}
        onSetFilterPhase={s.setFilterPhase}
        filterDateRange={s.filterDateRange}
        onSetFilterDateRange={s.setFilterDateRange}
        filterCustomStart={s.filterCustomStart}
        onSetFilterCustomStart={s.setFilterCustomStart}
        filterCustomEnd={s.filterCustomEnd}
        onSetFilterCustomEnd={s.setFilterCustomEnd}
        showExcluded={s.showExcluded}
        onToggleShowExcluded={() => s.setShowExcluded(!s.showExcluded)}
        onApply={handleApplyFilter}
        onClearAll={handleClearAllFilters}
      />

      {/* Side drawer: Export */}
      <ScheduleExportPanel
        isOpen={s.exportPanelOpen}
        onClose={() => s.setExportPanelOpen(false)}
        projectName={projectName}
        items={s.filteredItems}
        dateRangeStart={dateRangeStart}
        dateRangeEnd={dateRangeEnd}
      />

      {/* Modals */}
      <ScheduleItemModal
        show={s.showItemModal}
        editItem={s.editItem}
        links={s.links}
        includedItems={s.includedItems}
        scheduleItems={s.items}
        projectId={projectId}
        onClose={() => { s.setShowItemModal(false); s.setEditItem(null); s.clearCreateDefaults(); s.clearMessages(); }}
        onSave={wrappedHandleSaveItem}
        onDelete={(itemId) => { s.handleDelete(itemId); s.setShowItemModal(false); s.setEditItem(null); }}
        onUpsertLink={s.handleUpsertLink}
        onRemoveLink={s.handleRemoveLink}
        onRecalcLinkedItems={s.handleRecalc}
        hasSuccessors={s.editItem ? s.hasSuccessors(s.editItem.id) : false}
        saving={s.saving}
        savingLink={s.savingLink}
        deletingLinkId={s.deletingLinkId}
        saveError={s.saveError}
        saveSuccess={s.saveSuccess}
        settings={s.defaultSettings}
        holidays={s.allHolidays}
        canEdit={s.canEditReal}
        viewerRole={s.effectiveRole}
        checklistProgress={s.editItem ? s.checklistProgressMap.get(s.editItem.id) ?? null : null}
        createDefaults={s.createDefaults}
        onChecklistChanged={() => {
          s.reloadChecklistProgress();
        }}
      />

      <ScheduleGanttEditModal
        ganttEdit={s.ganttEdit}
        links={s.links}
        items={s.items}
        onClose={() => { s.setGanttEdit(null); s.clearMessages(); }}
        onSave={s.handleGanttEditSave}
        saving={s.saving}
        saveError={s.saveError}
        projectId={projectId}
        userId={user?.id}
        userRole={profile?.user_type}
      />

      <ScheduleGenerateModal
        show={s.showGenerateModal}
        includedItems={s.includedItems}
        settings={s.defaultSettings}
        holidays={s.allHolidays}
        saving={s.saving}
        saveError={s.saveError}
        onClose={() => s.setShowGenerateModal(false)}
        onGenerate={s.handleGenerateTimeline}
      />

      <ScheduleBaselineModal
        show={s.showBaselineModal}
        hasBaseline={hasBaseline}
        saving={s.saving}
        onClose={() => s.setShowBaselineModal(false)}
        onSave={s.handleSaveBaseline}
      />

      <ScheduleLinkModal
        show={s.showLinkModal}
        editItem={s.editItem}
        includedItems={s.items}
        saving={s.savingLink}
        saveError={s.saveError}
        onClose={() => { s.setShowLinkModal(false); s.clearMessages(); }}
        onSave={s.handleAddLink}
      />

      <ScheduleSettingsModal
        show={s.showSettingsModal}
        settings={s.settings.id ? s.settings : null}
        settingsForm={s.settingsForm}
        projectHolidays={s.projectHolidays}
        projectId={projectId}
        saving={s.saving}
        savingProjHoliday={s.savingProjHoliday}
        saveError={s.saveError}
        onClose={() => s.setShowSettingsModal(false)}
        onSaveSettings={s.handleSaveSettings}
        onSettingsFormChange={s.setSettingsForm}
        onLoadHolidays={s.reloadHolidays}
        onAddProjectHoliday={s.handleAddProjectHoliday}
        onRemoveProjectHoliday={s.handleRemoveProjectHoliday}
      />

      <BuildertrendImportModal
        projectId={projectId}
        existingItems={s.items}
        settings={s.defaultSettings}
        holidays={s.allHolidays}
        show={showImportModal}
        onClose={() => setShowImportModal(false)}
        onSaved={() => {
          setShowImportModal(false);
          s.reload();
        }}
      />

      {/* Mark All Pending Complete Confirmation Modal */}
      {showMarkAllConfirm && pendingCount > 0 && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowMarkAllConfirm(false)}></div>
          <div className="relative bg-white w-full max-w-md rounded-3xl overflow-hidden mx-4">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-[#1A1A1A] font-bold text-base">{t('schedule.markAllConfirmTitle')}</h3>
              <button onClick={() => setShowMarkAllConfirm(false)} className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 cursor-pointer">
                <i className="ri-close-line text-[#374151]"></i>
              </button>
            </div>
            <div className="px-5 py-4 space-y-3">
              <p className="text-[#6B7280] text-sm leading-relaxed">
                {t('schedule.markAllConfirmDesc', { count: pendingCount })}
              </p>
              <p className="text-[#92400E] text-xs bg-[#FEF0E7] rounded-xl p-2.5">
                <i className="ri-alert-line mr-1"></i>
                {t('schedule.markAllWarningText')}
              </p>
            </div>
            <div className="px-5 py-4 border-t border-gray-100 flex gap-2">
              <button
                onClick={() => setShowMarkAllConfirm(false)}
                className="flex-1 py-3 rounded-xl bg-white border border-gray-200 text-[#374151] text-sm font-bold cursor-pointer whitespace-nowrap"
              >
                {t('schedule.cancelBtn')}
              </button>
              <button
                onClick={async () => {
                  await s.handleMarkAllPendingComplete();
                  setShowMarkAllConfirm(false);
                  if (stageHookEnabled) {
                    await reloadStages();
                  }
                  checkAndShowStagePrompt();
                  onScheduleUpdated?.(); // Notify header to reload
                }}
                disabled={s.bulkMarking}
                className="flex-1 py-3 rounded-xl bg-[#059669] text-white text-sm font-bold cursor-pointer whitespace-nowrap disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {s.bulkMarking ? <><i className="ri-loader-4-line animate-spin"></i>{t('schedule.markingLabel')}</> : t('schedule.markCountCompleteBtn', { count: pendingCount })}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Copy Gantt Colors from 5 Methuen Road Confirmation Modal */}
      {showCopyColorsConfirm && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowCopyColorsConfirm(false)}></div>
          <div className="relative bg-white w-full max-w-md rounded-3xl overflow-hidden mx-4">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-[#1A1A1A] font-bold text-base">{t('schedule.copyColorsConfirmTitle')}</h3>
              <button onClick={() => setShowCopyColorsConfirm(false)} className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 cursor-pointer">
                <i className="ri-close-line text-[#374151]"></i>
              </button>
            </div>
            <div className="px-5 py-4 space-y-3">
              <p className="text-[#6B7280] text-sm leading-relaxed">
                {t('schedule.copyColorsConfirmDesc')}
              </p>
              <p className="text-[#92400E] text-xs bg-[#FEF0E7] rounded-xl p-2.5">
                <i className="ri-alert-line mr-1"></i>
                {t('schedule.copyColorsWarningText')}
              </p>
            </div>
            <div className="px-5 py-4 border-t border-gray-100 flex gap-2">
              <button
                onClick={() => setShowCopyColorsConfirm(false)}
                className="flex-1 py-3 rounded-xl bg-white border border-gray-200 text-[#374151] text-sm font-bold cursor-pointer whitespace-nowrap"
              >
                {t('schedule.cancelBtn')}
              </button>
              <button
                onClick={handleCopyColors}
                disabled={copyColorsLoading}
                className="flex-1 py-3 rounded-xl bg-[#EC2590] text-white text-sm font-bold cursor-pointer whitespace-nowrap disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {copyColorsLoading ? <><i className="ri-loader-4-line animate-spin"></i>{t('schedule.copyingColorsLabel')}</> : t('schedule.copyColorsBtn')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Copy Gantt Colors result toast */}
      {copyColorsResult && (
        <div className="flex-shrink-0 px-4 pt-3">
          <div className={`rounded-xl p-3 border flex items-start gap-2 ${copyColorsResult.success ? 'bg-[#F0FDF4] border-[#BBF7D0]' : 'bg-[#FEF2F2] border-[#FECACA]'}`}>
            <i className={`${copyColorsResult.success ? 'ri-checkbox-circle-line text-[#059669]' : 'ri-error-warning-line text-[#EF4444]'} text-sm flex-shrink-0 mt-0.5`}></i>
            <div className="flex-1">
              <p className={`text-sm ${copyColorsResult.success ? 'text-[#1A1A1A]' : 'text-[#EF4444]'}`}>{copyColorsResult.message}</p>
              <button onClick={() => setCopyColorsResult(null)} className="text-[#9CA3AF] text-xs mt-1 cursor-pointer hover:text-[#374151] transition-colors">
                {t('schedule.dismissResult')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Recalculate Critical Path Confirmation Modal */}
      {s.showRecalcConfirm && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => s.setShowRecalcConfirm(false)}></div>
          <div className="relative bg-white w-full max-w-md rounded-3xl overflow-hidden mx-4">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-[#1A1A1A] font-bold text-base">{t('schedule.recalcCriticalPathTitle')}</h3>
              <button onClick={() => s.setShowRecalcConfirm(false)} className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 cursor-pointer">
                <i className="ri-close-line text-[#374151]"></i>
              </button>
            </div>
            <div className="px-5 py-4 space-y-3">
              <p className="text-[#6B7280] text-sm leading-relaxed">
                {t('schedule.recalcCriticalPathDesc')}
              </p>
              <p className="text-[#92400E] text-xs bg-[#FEF0E7] rounded-xl p-2.5">
                <i className="ri-alert-line mr-1"></i>
                {t('schedule.recalcCriticalPathWarning')}
              </p>
            </div>
            <div className="px-5 py-4 border-t border-gray-100 flex gap-2">
              <button
                onClick={() => s.setShowRecalcConfirm(false)}
                className="flex-1 py-3 rounded-xl bg-white border border-gray-200 text-[#374151] text-sm font-bold cursor-pointer whitespace-nowrap"
              >
                {t('schedule.cancelBtn')}
              </button>
              <button
                onClick={s.confirmRecalcCriticalPath}
                disabled={s.saving}
                className="flex-1 py-3 rounded-xl bg-[#E8621A] text-white text-sm font-bold cursor-pointer whitespace-nowrap disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {s.saving ? <><i className="ri-loader-4-line animate-spin"></i>{t('schedule.calculatingLabel')}</> : t('schedule.recalcAndSaveBtn')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Completion Block Modal */}
      {s.completionBlockItem && (
        <ScheduleCompletionBlockModal
          show={!!s.completionBlockItem}
          itemTitle={s.completionBlockItem.title}
          progress={
            s.checklistProgressMap.get(s.completionBlockItem.id) ?? {
              totalLinked: 0,
              completedCount: 0,
              managementCheckedCount: 0,
              subcontractorCheckedCount: 0,
              requiredCount: 0,
              completedRequiredCount: 0,
              progressPct: 0,
              allRequiredComplete: true,
              hasLinkedItems: false,
              missingManagement: 0,
              missingSubcontractor: 0,
              blockedMessage: null,
              readyToComplete: false,
            }
          }
          onConfirmManualComplete={() => {
            if (s.completionBlockItem) {
              wrappedHandleToggleComplete(s.completionBlockItem, true);
            }
            s.setCompletionBlockItem(null);
          }}
          onClose={() => s.setCompletionBlockItem(null)}
          canManage={s.isManagement}
        />
      )}
    </div>
  );
}
